<template>
  <article class="user-request-card request-card">
    <div class="request-card__media">
      <img :alt="item.name || $t('general.user_requests')"
           :src="item.cover_image_link"
      />
    </div>

    <div class="request-card__content">
      <p v-if="item.training_type && item.training_type.name"
         class="request-card__category"
      >
        {{ item.training_type.name }}
      </p>
      <h2 :title="item.name" class="request-card__title">
        {{ item.name }}
      </h2>

      <div class="request-card__actions">
        <button class="request-card__button request-card__button--secondary"
                type="button"
                @click="$emit('select', item.id)"
        >
          <i aria-hidden="true" class="uil-search"></i>
          <span>{{ $t("general.review_demo") }}</span>
        </button>
        <button class="request-card__button request-card__button--primary"
                type="button"
                @click="$emit('select', item.id)"
        >
          <i aria-hidden="true" class="uil-cog"></i>
          <span>{{ $t("general.details") }}</span>
        </button>
      </div>
    </div>

    <footer class="request-card__footer">
      <i aria-hidden="true" class="uil-bell"></i>
      <span>{{
          $t("general.users_made_request", {
            count: item.users_pending_requests || 0,
          })
        }}</span>
    </footer>
  </article>
</template>

<script>
export default {
  name: "UserRequestCard",
  props: {
    item: {
      type: Object,
      default: () => ({}),
    },
  },
};
</script>

<style scoped>
.request-card {
  display: flex;
  min-width: 0;
  overflow: hidden;
  flex-direction: column;
  border: 0;
  border-radius: 14px;
  background: var(--request-surface);
  box-shadow: 0 2px 8px rgba(25, 31, 52, 0.08);
}

.request-card__media {
  overflow: hidden;
  aspect-ratio: 16 / 9;
  background: var(--request-soft);
}

.request-card__media img {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.request-card__content {
  display: flex;
  flex: 1;
  flex-direction: column;
  padding: 16px;
}

.request-card__category {
  overflow: hidden;
  margin: 0 0 4px;
  color: var(--request-muted);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.5;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.request-card__title {
  display: -webkit-box;
  overflow: hidden;
  min-height: 48px;
  margin: 0 0 16px;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  color: var(--request-text);
  font-size: 17px;
  font-weight: 600;
  line-height: 1.4;
  overflow-wrap: anywhere;
}

.request-card__actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
  margin-top: auto;
}

.request-card__button {
  display: inline-flex;
  width: 100%;
  align-items: center;
  justify-content: center;
  min-width: 0;
  min-height: 44px;
  padding: 8px 12px;
  border: 1px solid transparent;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  text-align: center;
  touch-action: manipulation;
  transition: opacity 0.15s ease, transform 0.15s ease;
  -webkit-tap-highlight-color: transparent;
}

.request-card__button i {
  flex: 0 0 auto;
  margin-right: 4px;
  font-size: 18px;
}

.request-card__button span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.request-card__button--secondary {
  border-color: #cfd5df;
  background: var(--request-surface);
  color: #424b5f;
}

.request-card__button--primary {
  border-color: var(--request-primary);
  background: var(--request-primary);
  color: #ffffff;
}

.request-card__button:focus-visible {
  outline: 3px solid rgba(62, 65, 109, 0.24);
  outline-offset: 2px;
}

.request-card__button:active {
  opacity: 0.85;
  transform: scale(0.98);
}

.request-card__footer {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-height: 56px;
  padding: 12px 16px;
  border-top: 1px solid #f0d9da;
  background: #fff7f7;
  color: #b4232f;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.45;
}

.request-card__footer i {
  flex: 0 0 auto;
  margin-top: 1px;
  font-size: 18px;
}

@media (max-width: 767px) {
  .request-card {
    border-radius: 12px;
  }

  .request-card__content {
    padding: 12px;
  }

  .request-card__category {
    font-size: 12px;
  }

  .request-card__title {
    min-height: 40px;
    margin-bottom: 12px;
    font-size: 14px;
    line-height: 1.4;
  }

  .request-card__actions {
    gap: 6px;
  }

  .request-card__button {
    min-height: 44px;
    padding: 7px 8px;
    font-size: 12px;
  }

  .request-card__button i {
    margin-right: 2px;
    font-size: 16px;
  }

  .request-card__footer {
    min-height: 52px;
    gap: 5px;
    padding: 10px 12px;
    font-size: 12px;
  }

  .request-card__footer i {
    font-size: 16px;
  }
}

@media (hover: hover) and (pointer: fine) {
  .request-card {
    transition: transform 0.18s ease, box-shadow 0.18s ease;
  }

  .request-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(25, 31, 52, 0.12);
  }

  .request-card__button--secondary:hover {
    border-color: var(--request-primary);
    color: var(--request-primary);
  }

  .request-card__button--primary:hover {
    opacity: 0.92;
  }
}

@media (prefers-reduced-motion: reduce) {
  .request-card,
  .request-card__button {
    transition: none;
    animation: none;
  }
}
</style>
